import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/components/ui/use-toast';
import { useRef, useState } from 'react';

interface ProgressUpdate {
  type: 'video' | 'audio' | 'reading' | 'quiz';
  completed?: boolean;
  score?: number;
  completed_at?: string;
}

interface ProgressResult {
  total_points: number;
  attempts: number;
  points_earned: number;
}

export function useLessonProgress(lessonId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    points: {
      video: number;
      audio: number;
      reading: number;
      quiz: number;
      total: number;
    };
    activitiesCompleted: {
      video: boolean;
      audio: boolean;
      reading: boolean;
      quiz: boolean;
    };
    cerebrixEarned: number;
  } | null>(null);

  const updateProgress = useMutation({
    mutationFn: async (data: ProgressUpdate): Promise<ProgressResult> => {
      if (!user) throw new Error('Usuario no autenticado');

      try {
        console.log('Estado inicial antes de actualizar:', {
          lessonId,
          tipo: data.type,
          datosRecibidos: data
        });

        // 1. Verificar progreso existente
        const { data: existingProgress, error: progressError } = await supabase
          .from('lesson_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('lesson_id', lessonId)
          .single();

        console.log('Datos de intentos:', {
          existingProgress,
          progressError
        });

        if (progressError && progressError.code !== 'PGRST116') {
          throw progressError;
        }

        // 2. Calcular puntos según el tipo de actividad
        let pointsEarned = 0;
        let attempts = existingProgress?.attempts;
        
        // Si no existe progreso previo, inicializar attempts en 0
        if (attempts === undefined || attempts === null) {
          attempts = 0;
        }
        
        // Solo incrementar attempts si es un quiz
        if (data.type === 'quiz') {
          attempts += 1;
        }

        console.log('Datos a actualizar:', {
          user_id: user.id,
          lesson_id: lessonId,
          attempts,
          updated_at: new Date().toISOString()
        });

        // Preparar datos base para actualizar
        const progressData: any = {
          user_id: user.id,
          lesson_id: lessonId,
          attempts,
          updated_at: new Date().toISOString()
        };

        // Obtener puntos actuales
        const currentPoints = {
          video: existingProgress?.video_points || 0,
          audio: existingProgress?.audio_points || 0,
          reading: existingProgress?.reading_points || 0,
          quiz: existingProgress?.last_quiz_score || 0
        };

        // Verificar si realmente se completó la actividad
        const isActivityCompleted = (type: string, progress: any): boolean => {
          switch (type) {
            case 'video':
              return progress?.video_watched === true;
            case 'audio':
              // Aquí deberíamos verificar si realmente se escuchó el audio completo
              return progress?.audio_listened === true && data.completed === true;
            case 'reading':
              return progress?.content_read === true;
            case 'quiz':
              return true; // El quiz siempre da puntos según respuestas correctas
            default:
              return false;
          }
        };

        // Actualizar campos específicos según el tipo de actividad
        if (data.type === 'video') {
          progressData.video_watched = data.completed;
          // Dar puntos y notificar cuando se complete el video
          if (data.completed) {
            progressData.video_points = 30;
            pointsEarned = 30;
            // Solo mostrar notificación si no tenía puntos antes
            if (!existingProgress?.video_points) {
              // Si el video está en progreso (no completado totalmente)
              if (data.type === 'video' && !progressData.video_watched) {
                toast({
                  title: "¡Progreso guardado!",
                  description: "Has ganado 30 puntos por ver el video",
                  className: "bg-green-50 border-2 border-green-500 shadow-lg",
                });
              } else {
                toast({
                  title: "¡Video completado!",
                  description: "Has ganado 30 puntos",
                  className: "bg-green-50 border-2 border-green-500 shadow-lg",
                });
              }
            }
          }
        } else if (data.type === 'audio') {
          progressData.audio_listened = data.completed;
          // En cada intento, dar puntos y notificar solo una vez cuando se complete
          if (data.completed && !existingProgress?.audio_points) {
            progressData.audio_points = 20;
            pointsEarned = 20;
            toast({
              title: "¡Audio completado!",
              description: "Has ganado 20 puntos",
              className: "bg-green-50 border-2 border-green-500 shadow-lg",
            });
          }
        } else if (data.type === 'reading') {
          progressData.content_read = data.completed;
          // En cada intento, dar puntos y notificar solo una vez cuando se complete
          if (data.completed && !existingProgress?.reading_points) {
            progressData.reading_points = 10;
            pointsEarned = 10;
            toast({
              title: "¡Lectura completada!",
              description: "Has ganado 10 puntos",
              className: "bg-green-50 border-2 border-green-500 shadow-lg",
            });
          }
        } else if (data.type === 'quiz') {
          // El score es el número de respuestas correctas (0-10)
          const score = data.score || 0;
          progressData.score = score;
          
          // Calcular puntos del quiz (4 puntos por respuesta correcta)
          const puntosPorRespuesta = 4;
          const puntosQuiz = score * puntosPorRespuesta;
          progressData.last_quiz_score = puntosQuiz;
          pointsEarned = puntosQuiz;
          
          console.log('Puntos del quiz:', {
            respuestasCorrectas: score,
            puntosPorRespuesta,
            puntosTotal: puntosQuiz,
            explicacion: `${score} respuestas correctas × ${puntosPorRespuesta} puntos = ${puntosQuiz} puntos`
          });

          // Si el video está visto y completó el quiz, marcar la lección como completada
          if (currentPoints.video_watched) {
            console.log('Quiz respondido y video visto, marcando lección como completada');
            progressData.completed = true;
            progressData.completed_at = new Date().toISOString();
            // Al completar el quiz, resetear los puntos para permitir ganarlos de nuevo
            progressData.video_points = 0;
            progressData.audio_points = 0;
            progressData.reading_points = 0;
          } else {
            console.log('Quiz respondido pero falta ver el video completo');
          }
        }

        console.log('Datos a actualizar:', {
          user_id: user.id,
          lesson_id: lessonId,
          attempts,
          updated_at: new Date().toISOString()
        });

        // Obtener el progreso actual para calcular puntos totales
        const { data: currentProgress, error: fetchProgressError } = await supabase
          .from('lesson_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('lesson_id', lessonId)
          .single();

        console.log('Progreso actual en BD:', currentProgress);

        if (fetchProgressError && fetchProgressError.code !== 'PGRST116') {
          console.error('Error al obtener progreso actual:', fetchProgressError);
          throw fetchProgressError;
        }

        // Calcular puntos totales
        const newTotalPoints = calculateTotalPoints(currentProgress, data.type, data.score || 0);
        progressData.total_points = newTotalPoints;

        // Actualizar o insertar el progreso
        const { error: upsertError } = await supabase
          .from('lesson_progress')
          .upsert(progressData, {
            onConflict: 'user_id,lesson_id',
            ignoreDuplicates: false
          });

        if (upsertError) {
          console.error('Error al actualizar progreso:', upsertError);
          throw upsertError;
        }

        // Verificar datos después de la actualización
        const { data: updatedProgress } = await supabase
          .from('lesson_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('lesson_id', lessonId)
          .single();

        console.log('Datos actualizados en BD:', updatedProgress);

        // 5. Si ganó puntos, calcular y otorgar Cerebrix
        // Verificar si la lección está completada (video visto y quiz aprobado)
        console.log('Verificando condiciones de lección completada:', {
          isQuiz: data.type === 'quiz',
          quizScore: data.score,
          videoWatched: currentProgress?.video_watched,
          totalPoints: newTotalPoints,
          attempts,
          currentProgress
        });

        const isLessonCompleted = 
          data.type === 'quiz' && 
          currentProgress?.video_watched === true;

        console.log('Verificando completado de lección:', {
          isQuiz: data.type === 'quiz',
          quizScore: data.score,
          videoWatched: currentProgress?.video_watched,
          isCompleted: isLessonCompleted,
          puntosTotales: newTotalPoints
        });

        if (isLessonCompleted) {
          console.log('Lección completada. Calculando Cerebrix:', {
            totalPoints: newTotalPoints,
            attempts,
            videoWatched: currentProgress?.video_watched,
            videoPoints: currentProgress?.video_points,
            audioPoints: currentProgress?.audio_points,
            readingPoints: currentProgress?.reading_points,
            quizScore: updatedProgress.last_quiz_score,
            maxPossiblePoints: 'Video(30) + Audio(20) + Lectura(10) + Quiz(40) = 100'
          });

          try {
            // Verificar que tenemos el ID del usuario
            if (!user?.id) {
              throw new Error('Usuario no autenticado');
            }

            // Calcular Cerebrix ganados en esta lección
            const calculatedCerebrix = calculateCerebrixReward(newTotalPoints, attempts);
            console.log('Otorgando Cerebrix:', calculatedCerebrix);

            // Obtener el perfil actual para lesson_cerebrix
            const { data: profiles, error: profileError } = await supabase
              .from('profiles')
              .select('lesson_cerebrix')
              .eq('id', user.id);

            if (profileError) {
              console.error('Error al obtener perfil:', profileError);
              throw profileError;
            }

            const currentProfile = profiles?.[0];
            if (!currentProfile) {
              console.error('Perfil no encontrado para usuario:', user.id);
              throw new Error('Perfil no encontrado');
            }

            console.log('Perfil actual:', {
              lesson_cerebrix: currentProfile.lesson_cerebrix
            });

            // Calcular nuevos lesson_cerebrix
            const oldLessonCerebrix = currentProfile.lesson_cerebrix || 0;
            const newLessonCerebrix = oldLessonCerebrix + calculatedCerebrix;

            console.log('Verificando valores antes de actualizar:', {
              oldLessonCerebrix,
              calculatedCerebrix,
              newLessonCerebrix
            });

            // 1. Registrar la transacción de Cerebrix
            const { error: transactionError } = await supabase
              .from('cerebrix_transactions')
              .insert({
                user_id: user.id,
                amount: calculatedCerebrix,
                source: 'lesson',
                source_id: lessonId,
                transaction_type: 'earn',
                description: `Ganó ${calculatedCerebrix} Cerebrix por completar la lección con ${newTotalPoints} puntos (Intento ${attempts})`
              });

            if (transactionError) {
              console.error('Error al registrar transacción:', transactionError);
              throw transactionError;
            }

            // 2. Actualizar solo lesson_cerebrix en el perfil
            const { data: updatedProfile, error: updateError } = await supabase
              .from('profiles')
              .update({
                lesson_cerebrix: newLessonCerebrix,
                updated_at: new Date().toISOString()
              })
              .eq('id', user.id)
              .select()
              .single();

            if (updateError) {
              console.error('Error al actualizar perfil:', updateError);
              throw updateError;
            }

            // Invalidar queries para actualizar la UI
            queryClient.invalidateQueries({ queryKey: ['profile'] });
            queryClient.invalidateQueries({ queryKey: ['cerebrixStats'] });

            // Mostrar el resumen
            const summaryPoints = {
              video: currentProgress?.video_points || 0,
              audio: currentProgress?.audio_points || 0,
              reading: currentProgress?.reading_points || 0,
              quiz: currentProgress?.last_quiz_score || 0,
              total: newTotalPoints
            };

            const summaryActivities = {
              video: currentProgress?.video_watched || false,
              audio: currentProgress?.audio_listened || false,
              reading: currentProgress?.content_read || false,
              quiz: currentProgress?.last_quiz_score > 0
            };

            // Primero mostramos el resumen
            setSummaryData({
              points: summaryPoints,
              activitiesCompleted: summaryActivities,
              cerebrixEarned: calculatedCerebrix
            });
            setShowSummary(true);

            // Mostrar toast de éxito
            toast({
              title: "¡Lección completada!",
              description: `¡Felicitaciones! Has ganado ${calculatedCerebrix} Cerebrix`,
              className: "bg-green-50 border-2 border-green-500 shadow-lg",
              duration: 5000, // Dar más tiempo para que el usuario vea el mensaje
            });

            // Esperar un momento antes de cualquier redirección
            return new Promise((resolve) => {
              setTimeout(() => {
                resolve(true);
              }, 3000); // Esperar 3 segundos antes de permitir redirección
            });

          } catch (error) {
            console.error('Error al procesar Cerebrix:', error);
            throw error;
          }
        } else {
          console.log('Lección no completada aún:', {
            isQuiz: data.type === 'quiz',
            completed: data.completed,
            videoWatched: currentProgress?.video_watched,
            quizScore: currentProgress?.last_quiz_score
          });
        }

        // 6. Invalidar query de progreso de lección
        queryClient.invalidateQueries({ queryKey: ['lessonProgress', lessonId] });

        // 7. Mostrar toast con la recompensa
        if (pointsEarned > 0) {
          let message = '';
          const completedAt = new Date(data.completed_at || new Date().toISOString());
          
          // Si hay progreso existente, verificar el tiempo desde la última actualización
          const lastUpdate = existingProgress?.updated_at ? new Date(existingProgress.updated_at) : null;
          const timeDiff = lastUpdate ? completedAt.getTime() - lastUpdate.getTime() : Infinity;
          const MIN_TIME_BETWEEN_NOTIFICATIONS = 2000; // 2 segundos
          
          // Mostrar notificación si:
          // 1. No hay actualización previa, o
          // 2. Ha pasado suficiente tiempo desde la última notificación, o
          // 3. Es un quiz (siempre se muestra)
          if (!lastUpdate || timeDiff > MIN_TIME_BETWEEN_NOTIFICATIONS || data.type === 'quiz') {
            switch (data.type) {
              case 'video':
                message = `¡Has completado el video! (+${pointsEarned} puntos)`;
                break;
              case 'audio':
                message = `¡Has completado el audio! (+${pointsEarned} puntos)`;
                break;
              case 'reading':
                message = `¡Has completado la lectura! (+${pointsEarned} puntos)`;
                break;
              case 'quiz':
                message = `¡Has obtenido ${pointsEarned} puntos en el quiz!`;
                break;
            }

            toast({
              title: "¡Progreso guardado!",
              description: message,
              className: "bg-green-50 border-2 border-green-500 shadow-lg",
            });
          }
        }

        return {
          total_points: newTotalPoints,
          attempts,
          points_earned: pointsEarned
        };

      } catch (error) {
        console.error('Error al actualizar progreso:', error);
        toast({
          title: "Error",
          description: "Hubo un problema al guardar tu progreso. Por favor, intenta de nuevo.",
          variant: "destructive",
        });
        throw error;
      }
    }
  });

  const calculateTotalPoints = (currentProgress: any, type: string, score: number): number => {
    console.log('Entrada calculateTotalPoints:', { type, score, currentProgress });
    
    let totalPoints = 0;
    
    // Sumar puntos del video (30 puntos si está completado)
    if (currentProgress?.video_watched) {
      totalPoints += 30;
    }
    
    // Sumar puntos del audio (20 puntos si está completado)
    if (currentProgress?.audio_listened) {
      totalPoints += 20;
    }
    
    // Sumar puntos de lectura (10 puntos si está completado)
    if (currentProgress?.content_read) {
      totalPoints += 10;
    }
    
    // Sumar puntos del quiz (4 puntos por respuesta correcta)
    let quizPoints = 0;
    if (type === 'quiz') {
      quizPoints = score * 4;
    } else {
      quizPoints = currentProgress?.quiz_score ? currentProgress.quiz_score * 4 : 0;
    }
    totalPoints += quizPoints;

    console.log('Calculando puntos totales:', {
      type,
      video: currentProgress?.video_watched ? 30 : 0,
      audio: currentProgress?.audio_listened ? 20 : 0,
      reading: currentProgress?.content_read ? 10 : 0,
      quiz: quizPoints,
      totalPoints,
      actividades_completadas: {
        video: currentProgress?.video_watched || false,
        audio: currentProgress?.audio_listened || false,
        lectura: currentProgress?.content_read || false,
        quiz: quizPoints > 0
      }
    });
    
    return totalPoints;
  };

  // Actualizar el cálculo de Cerebrix para usar los puntos reales obtenidos
  const calculateCerebrixReward = (totalPoints: number, attempts: number): number => {
    console.log('Entrada calculateCerebrixReward:', { totalPoints, attempts });

    // Determinar divisor según el número de intentos
    let divisor;
    if (attempts === 1) {
      divisor = 10;  // Primer intento: puntos/10
    } else if (attempts === 2) {
      divisor = 15;  // Segundo intento: puntos/15
    } else {
      divisor = 20;  // Tercer intento o más: puntos/20
    }

    // Calcular Cerebrix basado en puntos totales reales
    const calculatedCerebrix = Math.floor(totalPoints / divisor);

    console.log('Calculando Cerebrix:', {
      points: totalPoints,
      attempts,
      divisor,
      calculatedCerebrix,
      puntos_reales: totalPoints
    });

    return calculatedCerebrix;
  };

  const closeSummary = () => {
    setShowSummary(false);
    setSummaryData(null);
  };

  return {
    updateProgress,
    calculateCerebrixReward,
    showSummary,
    summaryData,
    closeSummary
  };
}
